import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { ErrorCard } from '@/components/ErrorCard'
import { InlineText } from '@/components/patterns/InlineText'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { toast } from '@/lib/toast'
import { CategoryChip } from '../components/CategoryChip'
import { ColorSwatchPicker } from '../components/ColorSwatchPicker'
import { IconPicker } from '../components/IconPicker'
import { createCategory, listCategories, reorderCategories, updateCategory } from './api'
import { DeleteCategoryDialog } from './DeleteCategoryDialog'
import type { CategoryScope, CrmCategory } from './types'

const USAGE_NOUN: Record<CategoryScope, [string, string]> = {
  relatie: ['relatie', 'relaties'],
  afspraak: ['afspraak', 'afspraken'],
  lijst: ['lijst', 'lijsten'],
}

/** How many relations (or, for a list kind, lists) use the category. */
function usageTotal(row: CrmCategory): number {
  return row.usage.contacts + row.usage.organisations + (row.usage.lists ?? 0)
}

function errorText(caught: unknown, fallback: string): string {
  return caught instanceof Error ? caught.message : fallback
}

/** One scope of categories: live chip, name, colour, icon, usage, order, delete. */
export function CategoryTable({ scope }: { scope: CategoryScope }) {
  const [rows, setRows] = useState<CrmCategory[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [deleting, setDeleting] = useState<CrmCategory | null>(null)
  const [newName, setNewName] = useState('')
  const nameField = useRef<HTMLInputElement>(null)
  // A reorder is one request for the whole list, so a second one may only follow the
  // answer to the first: it would start from an order the server no longer has.
  const moving = useRef(false)
  // The arrow buttons by `<category id>:<-1 | 1>`, and the arrow to focus once the list
  // has been reordered. A keyboard user who moves a category down presses the same button
  // again, so the focus must stay on it; at an end of the list that button is disabled
  // and the focus would drop to the page. The same effect puts the focus back if a
  // browser drops it when the row is moved in the DOM.
  const arrows = useRef(new Map<string, HTMLButtonElement>())
  const refocus = useRef<{ id: string; delta: -1 | 1 } | null>(null)

  useEffect(() => {
    let cancelled = false
    listCategories(scope)
      .then((data) => {
        if (!cancelled) {
          setRows(data)
          setFailed(false)
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [scope, reloadKey])

  useEffect(() => {
    const wanted = refocus.current
    if (!wanted || !rows) return
    refocus.current = null
    const index = rows.findIndex((row) => row.id === wanted.id)
    if (index < 0) return
    // At an end of the list the arrow that was used is disabled: take the other one.
    const next = index + wanted.delta
    const delta = next < 0 || next >= rows.length ? -wanted.delta : wanted.delta
    arrows.current.get(`${wanted.id}:${delta}`)?.focus()
  }, [rows])

  function arrowRef(id: string, delta: -1 | 1) {
    return (node: HTMLButtonElement | null) => {
      if (node) arrows.current.set(`${id}:${delta}`, node)
      else arrows.current.delete(`${id}:${delta}`)
    }
  }

  function replace(updated: CrmCategory) {
    setRows((current) => current?.map((row) => (row.id === updated.id ? updated : row)) ?? null)
  }

  /** Saves one change; a failure is thrown for the caller to show where the change was made. */
  async function save(category: CrmCategory, patch: Parameters<typeof updateCategory>[1]) {
    replace(await updateCategory(category.id, patch))
  }

  /** For the pickers, which have no place of their own to show a failure. */
  function savePicked(category: CrmCategory, patch: Parameters<typeof updateCategory>[1]) {
    save(category, patch).catch((caught: unknown) => {
      toast.error(errorText(caught, 'Opslaan mislukt.'))
    })
  }

  async function move(index: number, delta: -1 | 1) {
    if (!rows || moving.current) return
    const target = index + delta
    if (target < 0 || target >= rows.length) return
    const ids = rows.map((row) => row.id)
    ;[ids[index], ids[target]] = [ids[target], ids[index]]
    refocus.current = { id: ids[target], delta }
    moving.current = true
    try {
      setRows(await reorderCategories(scope, ids))
    } catch (caught) {
      toast.error(errorText(caught, 'Volgorde opslaan mislukt.'))
      setReloadKey((key) => key + 1)
    } finally {
      moving.current = false
    }
  }

  async function add() {
    const name = newName.trim()
    if (!name) return
    try {
      const created = await createCategory({ scope, name })
      setRows((current) => [...(current ?? []), created])
      setNewName('')
      // The button is disabled again now: the focus stays where the next name goes.
      nameField.current?.focus()
    } catch (caught) {
      toast.error(errorText(caught, 'Toevoegen mislukt.'))
    }
  }

  if (failed) return <ErrorCard onRetry={() => setReloadKey((key) => key + 1)} />
  if (rows === null) {
    return (
      <div className="flex flex-col gap-2" aria-hidden>
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }

  const [one, many] = USAGE_NOUN[scope]
  return (
    <div className="flex flex-col gap-4">
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nog geen categorieën.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Voorbeeld</TableHead>
              <TableHead>Naam</TableHead>
              <TableHead>Kleur</TableHead>
              <TableHead>Icoon</TableHead>
              <TableHead>Gebruikt</TableHead>
              <TableHead>
                <span className="sr-only">Acties</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, index) => {
              const total = usageTotal(row)
              const usage = `${total} ${total === 1 ? one : many}`
              return (
                <TableRow key={row.id}>
                  <TableCell>
                    <CategoryChip category={row} />
                  </TableCell>
                  <TableCell>
                    <InlineText
                      label={`Naam van ${row.name}`}
                      value={row.name}
                      required
                      maxLength={60}
                      onSave={(name) => save(row, { name: name ?? row.name })}
                    />
                  </TableCell>
                  <TableCell>
                    <ColorSwatchPicker
                      label={`Kleur van ${row.name}`}
                      value={row.color}
                      onChange={(color) => savePicked(row, { color })}
                    />
                  </TableCell>
                  <TableCell>
                    <IconPicker
                      label={`Icoon van ${row.name}`}
                      value={row.icon}
                      onChange={(icon) => {
                        if (icon) savePicked(row, { icon })
                      }}
                    />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {scope === 'relatie' && row.usage.contacts > 0 ? (
                      <Link
                        to={`/relaties/contacten?category_id=${row.id}`}
                        className="underline-offset-2 hover:underline"
                      >
                        {usage}
                      </Link>
                    ) : (
                      usage
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button
                        ref={arrowRef(row.id, -1)}
                        type="button"
                        size="icon-sm"
                        variant="ghost"
                        aria-label={`${row.name} omhoog`}
                        disabled={index === 0}
                        onClick={() => void move(index, -1)}
                      >
                        <ArrowUp />
                      </Button>
                      <Button
                        ref={arrowRef(row.id, 1)}
                        type="button"
                        size="icon-sm"
                        variant="ghost"
                        aria-label={`${row.name} omlaag`}
                        disabled={index === rows.length - 1}
                        onClick={() => void move(index, 1)}
                      >
                        <ArrowDown />
                      </Button>
                      <Button
                        type="button"
                        size="icon-sm"
                        variant="ghost"
                        aria-label={`${row.name} verwijderen`}
                        onClick={() => setDeleting(row)}
                      >
                        <Trash2 />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
      <form
        className="flex max-w-md gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          void add()
        }}
      >
        <Input
          ref={nameField}
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          placeholder="Naam van de nieuwe categorie"
          aria-label="Naam nieuwe categorie"
          maxLength={60}
        />
        <Button type="submit" disabled={!newName.trim()}>
          <Plus />
          Categorie toevoegen
        </Button>
      </form>
      {deleting ? (
        <DeleteCategoryDialog
          key={deleting.id}
          category={deleting}
          options={rows.filter((row) => row.id !== deleting.id)}
          onClose={() => setDeleting(null)}
          onChanged={() => setReloadKey((key) => key + 1)}
        />
      ) : null}
    </div>
  )
}
